import { $service } from '@xt/client/experimental/request/service'
import $global from '@xt/client/store/global'
import moment from 'moment'

import type { BookConfig, PlatformConfigSuffix } from '@xt/client/types/book.d'

class BookManager {
  private cache: Map<string, BookConfig>

  private startKey: number = 0
  private rawImgList: Map<
    string,
    {
      rawUrl: string
      decodeUrl: string
      status: 'success' | 'error'
      time: string
    }
  >

  constructor() {
    this.cache = new Map()
    this.rawImgList = new Map()
  }

  async getBook(jsonFileName: `${string}-${PlatformConfigSuffix}.json`) {
    if (this.cache.has(jsonFileName)) {
      return this.cache.get(jsonFileName)!
    }

    const [error, dataSource] = await $service<BookConfig>({
      method: 'GET',
      url: `/mocks/${jsonFileName}`,
      baseURL: $global.production ? 'https://static.xuantong.cn' : 'https://testing-static.xuantong.cn'
    })

    if (error) {
      return null
    }

    this.cache.set(jsonFileName, dataSource)
    return dataSource
  }

  // 判断是否已经读取过
  public hasReadImgList(imgList: string[]): boolean {
    if (!imgList.length) return false
    return this.rawImgList.has(imgList[0])
  }

  public getReadedRawImgList(imgList: string[]) {
    return imgList.filter(item => this.rawImgList.has(item)).map(k => this.rawImgList.get(k)!.decodeUrl)
  }

  // 批量获取解密图片url
  private async batchMapImgUrls(imgUrl: string[]): Promise<string[]> {
    const res: string[] = await new Promise(res => setTimeout(() => res(imgUrl), 500))

    imgUrl.forEach((item, idx) => {
      this.rawImgList.set(item, {
        rawUrl: item,
        decodeUrl: res[idx],
        status: 'success',
        time: moment().format('YYYY-MM-DD HH:mm:ss')
      })
    })

    return res
  }

  // 刷新图片url
  public async getRefreshImgUrls(
    imgList: string[],
    startKey: number,
    options: { preloadCount: number } = {
      preloadCount: 4
    }
  ): Promise<string[]> {
    const { preloadCount } = options

    // 提取指定位置的图片链接
    const preloadList = imgList.slice(startKey, startKey + preloadCount)

    return await this.batchMapImgUrls(preloadList)
  }

  // 加载图片url
  public async getDecodeImgUrls(
    imgList: string[],
    data?: {
      url: string
      order: number
    },
    options: { preloadCount: number } = {
      preloadCount: 4
    }
  ): Promise<string[] | void> {
    if (data && this.rawImgList.has(data.url)) return

    const { preloadCount } = options

    // 提取指定位置的图片链接
    const preloadList = imgList.slice(this.startKey, this.startKey + preloadCount)

    this.startKey += preloadCount

    return await this.batchMapImgUrls(preloadList)
  }

  // 重置滚动指针
  public resetKey() {
    this.startKey = 0
  }

  // 离开页面，清空缓存
  public cleanData() {
    this.rawImgList.clear()
  }
}

const bookManager = new BookManager()

export default bookManager
